import * as React from 'react';
import { cn } from '@/shared/lib/utils';
import { isFirefoxBrowser, useSpeechToText } from '@/shared/hooks/useSpeechToText';

type TextareaProps = React.ComponentProps<'textarea'> & {
  label?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  enableSpeechToText?: boolean;
  autoGrow?: boolean;
};

type TextareaInputEvent = Parameters<
  NonNullable<React.ComponentProps<'textarea'>['onInput']>
>[0];

const MicIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      labelClassName,
      wrapperClassName,
      enableSpeechToText = true,
      autoGrow: autoGrowEnabled = true,
      id,
      onChange,
      onInput,
      rows,
      value,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const resolvedId = id ?? generatedId;
    const internalRef = React.useRef<HTMLTextAreaElement | null>(null);

    const mergedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const autoGrow = React.useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea || !autoGrowEnabled) return;

      textarea.style.height = 'auto';
      if (textarea.scrollHeight > 0) {
        const border = textarea.offsetHeight - textarea.clientHeight;
        textarea.style.height = `${textarea.scrollHeight + border}px`;
      }
    }, [autoGrowEnabled]);

    React.useLayoutEffect(() => {
      autoGrow();
    }, [autoGrow, value, defaultValue]);

    React.useEffect(() => {
      const textarea = internalRef.current;
      if (!textarea || !autoGrowEnabled) return;

      let lastWidth = -1;
      const observer = new ResizeObserver(() => {
        if (textarea.clientWidth === lastWidth) return;
        lastWidth = textarea.clientWidth;
        autoGrow();
      });

      observer.observe(textarea);
      return () => observer.disconnect();
    }, [autoGrow, autoGrowEnabled]);

    const handleInput = React.useCallback(
      (event: TextareaInputEvent) => {
        autoGrow();
        onInput?.(event);
      },
      [autoGrow, onInput],
    );

    const { isListening, isTranscribing, isSupported, toggleListening } = useSpeechToText(internalRef);
    const showMic = enableSpeechToText && isSupported;

    const textarea = (
      <div className="relative">
        <textarea
          ref={mergedRef}
          id={resolvedId}
          data-th-typography="form-control"
          className={cn(
            'min-h-[74px] w-full rounded-xl border border-solid border-input-border bg-white px-4 py-3 font-sans text-form-control font-normal tracking-normal text-input-text placeholder:font-sans placeholder:tracking-normal placeholder:text-input-text/70 shadow-none ring-0 transition-[border-color,box-shadow] duration-200 ease-out outline-none focus:border-primary focus:shadow-none focus:ring-2 focus:ring-primary/20 focus-visible:border-primary focus-visible:shadow-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50',
            autoGrowEnabled
              ? 'max-h-[220px] resize-none overflow-y-auto'
              : 'resize-y',
            showMic && 'pr-12',
            className,
          )}
          onChange={onChange}
          onInput={handleInput}
          rows={autoGrowEnabled ? 1 : rows}
          value={value}
          defaultValue={defaultValue}
          {...props}
        />
        {showMic && (
          <button
            type="button"
            onClick={toggleListening}
            disabled={props.disabled || isTranscribing}
            aria-label={
              isTranscribing
                ? 'Transcribing audio...'
                : isListening
                  ? 'Stop recording'
                  : 'Start voice input'
            }
            title={
              isTranscribing
                ? 'Transcribing...'
                : isListening
                  ? 'Click to stop'
                  : 'Click to dictate'
            }
            className={cn(
              'absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
              isTranscribing
                ? 'cursor-wait bg-amber-100 text-amber-600'
                : isListening
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
              props.disabled && 'pointer-events-none opacity-50',
            )}
          >
            {isTranscribing ? (
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="31.4 31.4"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <MicIcon className="h-4 w-4" />
            )}
            {isListening && !isTranscribing && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
            )}
          </button>
        )}
        {enableSpeechToText && !isSupported && isFirefoxBrowser() && (
          <p className="mt-1 text-xs text-gray-400">
            Voice input is available in Chrome, Edge, and Safari.
          </p>
        )}
      </div>
    );

    if (!label) return textarea;

    return (
      <div className={cn('space-y-1.5', wrapperClassName)}>
        <label
          htmlFor={resolvedId}
          data-th-typography="form-label"
          className={cn('block text-form-label text-label', labelClassName)}
        >
          {label}
        </label>
        {textarea}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
export default Textarea;
